import { useMemo } from 'react';
import { Phone, LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './ui/sheet';
import { APP_TABS, PRIMARY_APP_TABS, SECONDARY_APP_TABS } from '../config/navigation';
import { usePanelSettings } from '../contexts/PanelSettingsContext';

const BrandLogo = () => (
  <div className="w-8 h-8 shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
    <Phone className="h-5 w-5 text-white" />
  </div>
);

/**
 * Тело меню — одно на десктопный сайдбар и мобильную выезжающую панель. Две копии разметки
 * разошлись бы так же, как когда-то разошлись реестры вкладок (см. `config/navigation.js`):
 * пункт, добавленный в одну, молча пропадал бы из другой.
 *
 * `collapsed` — только десктоп: полоса иконок, подписи уходят в `title` и `aria-label`.
 */
const SidebarContent = ({
  activeSection,
  onNavigate,
  onLogout,
  user,
  canAccess,
  collapsed = false,
  headerAction = null
}) => {
  const panels = usePanelSettings();

  // Названия разделов переименовываются в «Настройки» → «Интерфейс». Подменяем их здесь,
  // в одном месте: ниже метка уходит и в подпись, и в тултип свёрнутого меню, и в aria-label.
  const withLabel = (item) => ({ ...item, label: panels.tabLabel(item.id, item.label) });

  const navigationItems = useMemo(
    () => PRIMARY_APP_TABS.filter((item) => canAccess(item.id)).map(withLabel),
    [canAccess, panels]
  );

  const secondaryItems = useMemo(
    () => SECONDARY_APP_TABS.filter((item) => canAccess(item.id)).map(withLabel),
    [canAccess, panels]
  );

  return (
    <>
      {/* Логотип/Бренд */}
      {collapsed ? (
        <div className="flex justify-center py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-11 items-center">{headerAction}</div>
        </div>
      ) : (
        <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <BrandLogo />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Колл-центр</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Аналитика</p>
            </div>
          </div>
          {headerAction && <div className="ml-auto">{headerAction}</div>}
        </div>
      )}

      {/* Навигация */}
      <nav className={`flex-1 py-6 space-y-2 overflow-y-auto ${collapsed ? 'px-2' : 'px-4'}`}>
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <Button
                key={item.id}
                variant={isActive ? "secondary" : "ghost"}
                className={`w-full py-2 max-lg:h-10 ${collapsed ? 'justify-center px-0' : 'justify-start px-3'} ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
                onClick={() => onNavigate(item.id)}
                // Подписи пунктов переименовываются тенантом через panels.tabLabel, поэтому
                // машиночитаемая опора — id, а он уже дублируется в APP_TABS и AppUser.AllTabIds.
                data-section-id={item.id}
                data-ev="nav.section.select"
                data-ev-value={item.id}
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'} ${
                  isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`} />
                {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Button>
            );
          })}
        </div>

        <Separator className="my-6" />

        <div className="space-y-1">
          {secondaryItems.map((item) => {
            const Icon = item.icon;

            return (
              <Button
                key={item.id}
                variant="ghost"
                className={`w-full py-2 max-lg:h-10 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 ${
                  collapsed ? 'justify-center px-0' : 'justify-start px-3'
                }`}
                onClick={() => onNavigate(item.id)}
                // Подписи пунктов переименовываются тенантом через panels.tabLabel, поэтому
                // машиночитаемая опора — id, а он уже дублируется в APP_TABS и AppUser.AllTabIds.
                data-section-id={item.id}
                data-ev="nav.section.select"
                data-ev-value={item.id}
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
              >
                <Icon className={`h-4 w-4 ${collapsed ? '' : 'mr-3'}`} />
                {!collapsed && <span className="text-sm">{item.label}</span>}
              </Button>
            );
          })}
        </div>
      </nav>

      {/* Профиль пользователя */}
      <div className={`py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 mt-auto ${collapsed ? 'px-2' : 'px-4'}`}>
        <div
          className={`flex items-center mb-3 ${collapsed ? 'justify-center' : 'space-x-3'}`}
          title={collapsed ? user?.username : undefined}
        >
          {/* Картинки у аватара нет намеренно: аватары пользователей в системе не
              хранятся. Раньше здесь стоял <AvatarImage src="/api/placeholder/36/36">,
              и каждая загрузка страницы давала 401 в консоли — эндпоинта с таким
              путём не существует, а Radix всё равно откатывался на инициалы. */}
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {(user?.username ?? 'ИП').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.username ?? 'Иван Петров'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.role ?? 'Менеджер'}
              </p>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={`w-full py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 ${
            collapsed ? 'justify-center px-0' : 'justify-start px-2'
          }`}
          onClick={onLogout}
          data-ev="nav.logout"
          title={collapsed ? 'Выйти' : undefined}
          aria-label={collapsed ? 'Выйти' : undefined}
        >
          <LogOut className={`h-4 w-4 ${collapsed ? '' : 'mr-2'}`} />
          {!collapsed && <span className="text-sm">Выйти</span>}
        </Button>
      </div>
    </>
  );
};

/**
 * Десктопный сайдбар (от `lg`). Ниже `lg` его заменяют `MobileTopBar` и `MobileSidebar`:
 * на телефоне постоянная колонка в 256px оставляла контенту около сотни пикселей.
 *
 * Свёрнутое состояние — полоса иконок; развёрнутое совпадает с тем, как сайдбар выглядел
 * до появления кнопки, и остаётся умолчанием (`config/sidebarState.js`).
 */
const Sidebar = ({
  activeSection,
  onSectionChange,
  onLogout = () => {},
  user,
  canAccess,
  collapsed = false,
  onToggleCollapsed
}) => {
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  const toggleLabel = collapsed ? 'Развернуть меню' : 'Свернуть меню';

  return (
    <div
      className={`hidden lg:flex flex-col h-screen shrink-0 bg-white border-r border-gray-200 dark:bg-gray-900 dark:border-gray-700 sticky top-0 self-start transition-[width] duration-200 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <SidebarContent
        activeSection={activeSection}
        onNavigate={onSectionChange}
        onLogout={onLogout}
        user={user}
        canAccess={canAccess}
        collapsed={collapsed}
        headerAction={
          onToggleCollapsed && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              onClick={onToggleCollapsed}
              data-ev={collapsed ? 'nav.sidebar.expand' : 'nav.sidebar.collapse'}
              title={toggleLabel}
              aria-label={toggleLabel}
              aria-expanded={!collapsed}
            >
              <ToggleIcon className="h-4 w-4" />
            </Button>
          )
        }
      />
    </div>
  );
};

/**
 * Верхняя панель ниже `lg`: кнопка меню и название текущего раздела.
 *
 * `fixed`, а не `sticky`: у `main` стоит `overflow-y-auto`, он становится scroll-контейнером,
 * и sticky-панель внутри него прилипала бы к нему, а не к окну, — то есть уезжала бы вместе
 * со страницей.
 */
export const MobileTopBar = ({ activeSection, onOpenMenu }) => {
  const panels = usePanelSettings();
  const sectionLabel = panels.tabLabel(
    activeSection,
    APP_TABS.find((tab) => tab.id === activeSection)?.label
  );

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-gray-200 bg-white/95 px-3 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 lg:hidden">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-10 w-10 shrink-0"
        onClick={onOpenMenu}
        data-ev="nav.menu.open"
        aria-label="Открыть меню"
      >
        <Menu className="h-5 w-5" />
      </Button>
      <BrandLogo />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
          {sectionLabel ?? 'Колл-центр'}
        </p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">Колл-центр · Аналитика</p>
      </div>
    </header>
  );
};

/** Выезжающее меню ниже `lg`. Выбор пункта закрывает панель и возвращает страницу наверх. */
export const MobileSidebar = ({
  open,
  onOpenChange,
  activeSection,
  onSectionChange,
  onLogout = () => {},
  user,
  canAccess
}) => {
  const handleNavigate = (sectionId) => {
    onSectionChange(sectionId);
    onOpenChange(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        trackId="nav.mobile_menu"
        side="left"
        className="flex w-72 max-w-[85vw] flex-col gap-0 bg-white p-0 dark:bg-gray-900"
      >
        {/* Radix требует заголовок и описание у диалога; визуально их роль играет бренд. */}
        <SheetTitle className="sr-only">Меню</SheetTitle>
        <SheetDescription className="sr-only">Разделы приложения</SheetDescription>
        <SidebarContent
          activeSection={activeSection}
          onNavigate={handleNavigate}
          onLogout={onLogout}
          user={user}
          canAccess={canAccess}
        />
      </SheetContent>
    </Sheet>
  );
};

export default Sidebar;
